import React, { Component } from 'react';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import { Link } from 'react-router-dom';
import { withGoogleMap, GoogleMap, Marker } from 'react-google-maps';

import LocationActionCreators from 'actions/LocationActionCreators';
import Roles from 'constants/UserRoles';
import LocationStore from 'stores/LocationStore';
import SessionStore from 'stores/SessionStore';
import ToastStore from 'stores/ToastStore';
import { getMarkets } from 'utils/MarketAPIUtils';
import { updateLocation } from 'utils/LocationWebAPIUtils';

import Container from 'components/v2/container';
import Card from 'components/v2/card';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import EditButton from 'components/v2/buttons/EditButton';
import PlusIcon from 'components/v2/icons/PlusIcon';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { withTranslation } from 'react-i18next';
import CargomaticSelect from '../../v2/select';

const CAN_UPDATE = [Roles.LOCATIONS_MANAGER, Roles.LOCATIONS_ADMIN, Roles.GOD];

function mapRoles() {
  const roles = SessionStore.getUserRoles();
  const canUpdate = roles.some((r) => CAN_UPDATE.includes(r));

  return { canUpdate };
}

const LocationMap = withGoogleMap(({ lat, lng }) => (
  <GoogleMap defaultCenter={{ lat, lng }} defaultZoom={8} showMarker visible>
    <Marker position={{ lat, lng }} />
  </GoogleMap>
));

class Details extends Component {
  constructor(props) {
    super(props);

    this.state = {
      alias: '',
      position: {},
      showConfirmModal: false,
      currentLocationId: null,
      editing: false,
      market: '',
      markets: [],
      canUpdate: false,
      location: null,
    };
  }

  async fetchMarkets() {
    const {
      entity: { markets },
    } = await getMarkets();
    this.setState({ markets });
  }

  getStateFromStore() {
    const location = LocationStore.getLocation();
    const updates = {
      location,
    };
    if (location && location.market) {
      updates.market = location.market.code;
    }
    this.setState(updates);
  }

  componentDidMount() {
    LocationStore.addChangeListener(() => this.getStateFromStore());
    this.fetchMarkets();

    this.setRoles();
  }

  componentWillUnmount() {
    LocationActionCreators.clearLocation();
  }

  setRoles() {
    this.setState({ ...mapRoles() });
  }

  showNotifications(title, level) {
    ToastStore.getNotifications().push({
      title,
      body: '',
      level,
    });
    ToastStore.emitChange();
  }

  getRequest() {
    const market = this.state.markets.find((m) => m.code === this.state.market);
    return { ...this.state.location, market };
  }

  handleMarketChange = (market) => {
    this.setState({ market: market.code });
  };

  handleSaveMarket = async () => {
    const { t } = this.props;
    const market = this.state.markets.find((m) => m.code === this.state.market);
    if (!market) {
      return;
    }

    try {
      const location = this.getRequest();
      await updateLocation(location);
      this.showNotifications(t('MARKET_UPDATE_SUCCESS'), 'success');
      this.setState({ location });
    } catch (e) {
      this.showNotifications(e.message, 'error');
    } finally {
      this.setState({ editing: false });
    }
  };

  handleToggleEditMarket = () => {
    const { location } = this.state;
    this.setState((prevState) => {
      const editing = !prevState.editing;
      const market = editing ? location.market.code : '';
      return {
        editing,
        market,
      };
    });
  };

  deleteAlias(location, alias) {
    LocationActionCreators.deleteLocationAlias(location._id, alias);
  }

  handleAliasChange = (e) => {
    const {
      target: { value: alias },
    } = e;
    this.setState({
      alias,
    });
  };

  handleAddAlias = (e) => {
    e.preventDefault();
    const { location } = this.state;
    if (this.state.alias.length === 0) {
      return;
    }
    LocationActionCreators.addLocationAlias(location.id, this.state.alias);
    this.setState({ alias: '' });
  };

  handleConfirmDelete = () => {
    LocationActionCreators.removeLocation(this.state.currentLocationId);
    this.handleHideConfirmModal();
  };

  handleDelete = (currentLocationId) => {
    this.setState({
      currentLocationId,
      showConfirmModal: true,
    });
  };

  handleHideConfirmModal = () => {
    this.setState({
      showConfirmModal: false,
    });
  };

  render() {
    const { t } = this.props;
    if (!this.state.location) {
      return null;
    }

    const location = _.extend(this.state.location, this.state.position);
    const { aliases } = location;

    return (
      <Container className="py-10">
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-8">
          <div className="lg:col-span-2">
            <Link to="/locations" className="text-sm mb-4 underline block">
              <span>{t('BACK_TO_LOCATIONS')}</span>
            </Link>
            <Card
              header={() => (
                <div className="flex justify-between items-center">
                  <h3 className="my-0 truncate">
                    {location.name || t('DETAILS')}
                  </h3>
                  {this.state.canUpdate && (
                    <DeleteButton
                      onClick={() => this.handleDelete(location._id)}
                    />
                  )}
                </div>
              )}
              grayFooter
              footer={() => (
                <div>
                  <h3 className="text-lg font-medium leading-6 my-0">
                    {t('ALIASES')}
                  </h3>
                  {this.state.canUpdate && (
                    <form
                      className="mt-4"
                      action="#"
                      onSubmit={this.handleAddAlias}
                    >
                      <div className="flex justify-end">
                        <input
                          placeholder={t('ENTER_NEW_ALIAS')}
                          className="flex-1 block form-field rounded-r-none"
                          type="text"
                          name="alias"
                          value={this.state.alias}
                          onChange={this.handleAliasChange}
                        />
                        <button
                          type="submit"
                          disabled={this.state.alias.length === 0}
                          title={t('ADD_ALIAS')}
                          className="button button-green rounded-l-none"
                        >
                          <div className="flex items-center justify-center">
                            <PlusIcon />
                          </div>
                        </button>
                      </div>
                    </form>
                  )}
                  <ul className="mt-4 divide-y divide-gray-200 bg-white rounded-md shadow">
                    {aliases.map((alias, i) => (
                      <li
                        className="px-4 py-4 flex items-center justify-between"
                        key={i}
                      >
                        <span className="text-sm">{alias}</span>
                        {this.state.canUpdate && (
                          <DeleteButton
                            size="small"
                            onClick={() => this.deleteAlias(location, alias)}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            >
              <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <DescriptionListItem
                  className="sm:col-span-full"
                  label={t('ADDRESS')}
                >
                  <div className="text-sm truncate">{location.address}</div>
                </DescriptionListItem>
                <DescriptionListItem
                  classname="sm:col-span-1"
                  label={t('TYPE')}
                >
                  <div className="text-sm">{location._sub_type}</div>
                </DescriptionListItem>
                <DescriptionListItem
                  classname="sm:col-span-1"
                  label={t('LONG_LAT')}
                >
                  <div className="text-sm">
                    {location.lng}, {location.lat}
                  </div>
                </DescriptionListItem>
                <DescriptionListItem
                  className="sm:col-span-full"
                  label={titleize(t('MARKET', { count: 1 }))}
                >
                  {!this.state.editing && (
                    <div className="flex items-center space-x-2">
                      <span>{location.market.code}</span>
                      {this.state.canUpdate && (
                        <EditButton
                          size="small"
                          onClick={this.handleToggleEditMarket}
                        />
                      )}
                    </div>
                  )}
                  {this.state.editing && (
                    <div className="flex flex-wrap -mx-2">
                      <CargomaticSelect
                        className="px-2"
                        hideLabel
                        onSelect={this.handleMarketChange}
                        items={this.state.markets}
                        selected={this.state.market}
                        valueKey="code"
                        textKey="name"
                      />
                      <div className="px-2 flex space-x-1">
                        <button
                          className="button button-green"
                          onClick={this.handleSaveMarket}
                        >
                          {titleize(t('SAVE'))}
                        </button>
                        <button
                          className="button button-gray"
                          onClick={this.handleToggleEditMarket}
                        >
                          {titleize(t('CANCEL'))}
                        </button>
                      </div>
                    </div>
                  )}
                </DescriptionListItem>
              </dl>
            </Card>
          </div>
          <div className="lg:col-span-2">
            <LocationMap
              loadingElement={<div style={{ height: `100%` }} />}
              containerElement={<div style={{ height: '100%' }} />}
              mapElement={<div style={{ height: `100%` }} />}
              lat={location.lat}
              lng={location.lng}
            />
          </div>
        </div>
        <ConfirmModal
          show={this.state.showConfirmModal}
          title={t('DELETE_CONFIRMATION')}
          message={t('DELETE_LOCATION_CONFIRMATION')}
          onConfirm={this.handleConfirmDelete}
          onHide={this.handleHideConfirmModal}
        />
      </Container>
    );
  }
}
export default withTranslation()(Details);
